<template>
  <div class="flex justify-between px-2 py-4 text-xs rounded bg-primary-50">
    <ul class="md:flex md:flex-wrap">
      <li
        v-for="filter in appliedFilters"
        :key="filter.name"
        class="flex flex-wrap items-center mb-1 leading-4 md:first:border-l-0 md:border-l md:border-l-solid md:border-l-gray-400 md:pr-1 md:pl-2"
      >
        <span class="mr-1">{{ attributeDisplayName(filter) + ': ' }}</span>
        <div
          v-for="filterValue in attributeValuesDisplay(filter.name, filter.items)"
          :key="filterValue.item"
          class="flex items-center mr-4 font-bold cursor-pointer"
          @click="toggleFilter(filter.name, filterValue.item)"
        >
          <span class="mr-1" :class="{ 'text-gray-500': filterValue.count === 0 }">{{
            displayValue(filter.name, filterValue.item)
          }}</span>
          <CIcon name="close" />
        </div>
      </li>
    </ul>
    <span
      class="self-end px-6 font-semibold text-sm text-primary-900 cursor-pointer min-w-[60px] md:self-center"
      @click="clearAll()"
      >{{ t('clear_all') }}</span
    >
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { LocationQueryValue } from '#vue-router';
import { useProductStore } from '@/stores';

const SUPPLY_GROUP = 'supplyGroup';
const BRAND = 'brand';
const CLASSIFICATION_NAME = 'classificationName';
const CATEGORY_LEAFS = 'categoryLeafs';
const ETIM_CLASS = 'etimClass';

const { t } = useI18n();
const { appliedFilters } = storeToRefs(useProductStore());
const route = useRoute();
const router = useRouter();

const toggleFilter = (filterName: string, filterValue: string) => {
  const query = { ...route.query };
  const filterValues = Array.isArray(query[filterName])
    ? [...(query[filterName] as LocationQueryValue[])]
    : [query[filterName]];

  if (filterName === SUPPLY_GROUP) {
    filterValues.length = 0;
  } else if (filterName === STOCKED_AT_NAME) {
    filterValues.length = 0;
  } else {
    const valueIndex = filterValues.findIndex((existingValue) => existingValue === filterValue.toString());
    filterValues.splice(valueIndex, 1);
  }

  if (!filterValues.length) {
    delete query[filterName];
  } else {
    Object.assign(query, { [filterName]: filterValues });
  }

  const newRoute = {
    path: route.path,
    query,
  };

  router.push(newRoute);
};

const clearAll = () => {
  // We dont want to keep page, q is only used on the search page but should be fine to keep
  const queriesToKeep = ['q', 'sort', 'perPage'];

  const query = Object.fromEntries(Object.entries(route.query).filter(([key]) => queriesToKeep.includes(key)));

  const newRoute = {
    path: route.path,
    query,
  };

  router.push(newRoute);
};

const attributeDisplayName = (filter: { name: string; label: string }) => {
  switch (filter.name) {
    case SUPPLY_GROUP:
    case STOCKED_AT_NAME:
      return t('show_only_stocked_products');
    case BRAND:
      return t('brand');
    case CLASSIFICATION_NAME:
      return t('classification_quality_labeling');
    case CATEGORY_LEAFS:
      return t('category');
    case ETIM_CLASS:
      return t('etim_class');
    default:
      return filter.label || filter.name;
  }
};

const attributeValuesDisplay = (
  filterName: string,
  filterValues: { count: number; item: string }[],
): { item: string; selected?: boolean; count?: number }[] => {
  if (filterName === SUPPLY_GROUP) {
    const updatedStockFilterValues = {
      count: filterValues[0].count + filterValues[1]?.count,
      item: t('yes'),
      selected: true,
    };

    return [updatedStockFilterValues];
  }
  if (filterName === STOCKED_AT_NAME) {
    const updatedStockFilterValues = {
      item: t('yes'),
      selected: true,
    };

    return [updatedStockFilterValues];
  }
  return filterValues;
};

const displayValue = (filterName: string, filterValue: string) => {
  if (filterName === 'categoryLeafs') {
    return filterValue.substring(filterValue.lastIndexOf('|') + 1);
  }
  return filterValue;
};
</script>

<script lang="ts">
export default defineComponent({
  name: 'ProductListingPageAppliedFilters',
});
</script>
